// Client-side /expansion tracker. Queries the expansion_* tables from the
// browser DB and renders the phase tracker (live phase state from real "now",
// not build time), the 2026 Entry Draft board, the per-team roster cards, and
// the sources list — mirroring templates/expansion.html and its includes.
// Pure helpers (phase/keydate state, roster sort, draft resolution, tags) live
// in expansion-logic.ts (parity-tested against expansion.py in Node).

import { getDbWorker } from "./db";
import { LEAGUE_NAMES } from "./draft-logic";
import {
  type DraftPick,
  type DraftSelection,
  type KeyDate,
  type Phase,
  type PickTrade,
  type Player,
  type Team,
  compareSortKeys,
  contractLabel,
  currentPhase,
  currentTeam,
  departedFrom,
  draftPicksFor,
  draftRounds,
  isDepartedFrom,
  isRetired,
  keydateState,
  latestMove,
  nextPhase,
  phaseState,
  rosterFor,
  rosterSortKey,
  tags,
} from "./expansion-logic";
import { teamColor, teamColorContrast, teamColorSoft } from "./team-colors";

// ---------------------------------------------------------------------------
// Small utilities (mirror Jinja autoescaping + app._initials + strftime)
// ---------------------------------------------------------------------------
function esc(s: string | number | null | undefined): string {
  if (s === null || s === undefined) return "";
  return String(s).replace(/&/g, "&amp;").replace(/</g, "&lt;")
    .replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
function initials(name: string): string {
  const parts = (name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "?";
  const first = parts[0]![0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]![0] ?? "") : "";
  return (first + last).toUpperCase();
}

const ET = "America/New_York";
function fmt(iso: string, opts: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat("en-US", { timeZone: ET, ...opts }).format(new Date(iso));
}
// strftime analogues used by the template, all rendered in ET.
const monDay = (iso: string): string => fmt(iso, { month: "short", day: "numeric" });
const monDayTime = (iso: string): string =>
  `${fmt(iso, { month: "short", day: "numeric" })} · ${fmt(iso, { hour: "numeric", minute: "2-digit", hour12: true })}`;
const monthDayYear = (iso: string): string =>
  fmt(iso, { month: "long", day: "numeric", year: "numeric" });

// ---------------------------------------------------------------------------
// Data model loaded from the browser DB
// ---------------------------------------------------------------------------
interface StatLine { display: string; title: string }
interface PhotoInfo { headshot_path: string | null; team_code: string | null }
interface ProspectMeta {
  name: string; position: string; club: string; league: string;
  nationality: string; ep_url: string;
}
interface ExpansionData {
  phases: Phase[];
  entryDraft: KeyDate | null;
  teams: Team[];
  players: Player[];
  trades: PickTrade[];
  selections: DraftSelection[];
  statsByPid: Map<number, StatLine>;
  photoByPid: Map<number, PhotoInfo>;
  prospectsByName: Map<string, ProspectMeta>; // casefold full name -> meta
  generalSources: [string, string][];
}

async function loadData(): Promise<ExpansionData> {
  const worker = await getDbWorker();
  const q = (sql: string): Promise<Array<Record<string, unknown>>> =>
    worker.db.query(sql) as Promise<Array<Record<string, unknown>>>;
  // expansion_player_stats ships in migration 0011; tolerate a browser DB built
  // before it landed (the cards then render without the stat line).
  const qSoft = async (sql: string): Promise<Array<Record<string, unknown>>> => {
    try { return await q(sql); } catch { return []; }
  };

  const [
    phaseRows, keyDateRows, teamRows, playerRows, moveRows,
    tradeRows, selRows, statRows, photoRows, prospectRows, sourceRows,
  ] = await Promise.all([
    q("SELECT number, name, start_ts, end_ts, summary FROM expansion_phases ORDER BY number"),
    q("SELECT phase_number, when_ts, label, audience, is_entry_draft, sort_order " +
      "FROM expansion_key_dates ORDER BY is_entry_draft, phase_number, sort_order, when_ts"),
    q("SELECT code, name, kind, note FROM expansion_teams ORDER BY sort_order, code"),
    q("SELECT id, name, position, origin_team, contract, player_id, jersey, ep_id " +
      "FROM expansion_players ORDER BY sort_order, id"),
    q("SELECT player_ref, phase, kind, to_team, note, source_url FROM expansion_moves " +
      "ORDER BY player_ref, seq, id"),
    q("SELECT round, slot, owner, note, source_url FROM expansion_draft_pick_trades ORDER BY round, slot"),
    q("SELECT round, slot, player, source_url, position, previous_team, nationality " +
      "FROM expansion_draft_selections ORDER BY round, slot"),
    qSoft("SELECT player_id, display, title FROM expansion_player_stats"),
    q("SELECT p.player_id AS pid, t.code AS team_code, pp.headshot_path AS headshot_path " +
      "FROM expansion_players ep JOIN players p ON p.player_id = ep.player_id " +
      "LEFT JOIN teams t ON t.team_id = p.current_team_id " +
      "LEFT JOIN player_profile pp ON pp.player_id = p.player_id"),
    q("SELECT last_name, first_name, position, nationality, club, league, ep_url " +
      "FROM expansion_prospects WHERE from_eligibility = 1 ORDER BY sort_order, id"),
    q("SELECT team_code, label, url FROM expansion_sources " +
      "ORDER BY team_code IS NOT NULL, team_code, sort_order, id"),
  ]);

  // Phases with nested key dates; the standalone Entry Draft pulled aside.
  const datesByPhase = new Map<number, KeyDate[]>();
  let entryDraft: KeyDate | null = null;
  for (const r of keyDateRows) {
    const kd: KeyDate = {
      when: String(r["when_ts"]), label: String(r["label"]), audience: String(r["audience"] ?? ""),
    };
    if (Number(r["is_entry_draft"]) === 1) {
      if (entryDraft === null) entryDraft = kd;
    } else if (r["phase_number"] !== null && r["phase_number"] !== undefined) {
      const n = Number(r["phase_number"]);
      (datesByPhase.get(n) ?? datesByPhase.set(n, []).get(n)!).push(kd);
    }
  }
  const phases: Phase[] = phaseRows.map((r) => ({
    number: Number(r["number"]), name: String(r["name"]),
    start: String(r["start_ts"]), end: String(r["end_ts"]),
    summary: String(r["summary"] ?? ""), dates: datesByPhase.get(Number(r["number"])) ?? [],
  }));

  const movesByRef = new Map<number, Player["moves"]>();
  for (const r of moveRows) {
    const ref = Number(r["player_ref"]);
    (movesByRef.get(ref) ?? movesByRef.set(ref, []).get(ref)!).push({
      phase: Number(r["phase"] ?? 0), kind: String(r["kind"]),
      to_team: r["to_team"] === null || r["to_team"] === undefined ? null : String(r["to_team"]),
      note: String(r["note"] ?? ""), source_url: String(r["source_url"] ?? ""),
    });
  }
  const players: Player[] = playerRows.map((r) => ({
    name: String(r["name"]), position: String(r["position"] ?? ""),
    origin_team: String(r["origin_team"]), contract: String(r["contract"] ?? ""),
    player_id: r["player_id"] === null || r["player_id"] === undefined ? null : Number(r["player_id"]),
    jersey: r["jersey"] ? String(r["jersey"]) : null,
    ep_id: r["ep_id"] ? String(r["ep_id"]) : null,
    moves: movesByRef.get(Number(r["id"])) ?? [],
  }));

  const teams: Team[] = teamRows.map((r) => ({
    code: String(r["code"]), name: String(r["name"]),
    kind: String(r["kind"]), note: String(r["note"] ?? ""),
  }));
  const trades: PickTrade[] = tradeRows.map((r) => ({
    round: Number(r["round"]), slot: Number(r["slot"]), owner: String(r["owner"]),
    note: String(r["note"] ?? ""), source_url: String(r["source_url"] ?? ""),
  }));
  const selections: DraftSelection[] = selRows.map((r) => ({
    round: Number(r["round"]), slot: Number(r["slot"]), player: String(r["player"]),
    source_url: String(r["source_url"] ?? ""), position: String(r["position"] ?? ""),
    previous_team: String(r["previous_team"] ?? ""), nationality: String(r["nationality"] ?? ""),
  }));

  const statsByPid = new Map<number, StatLine>();
  for (const r of statRows) {
    statsByPid.set(Number(r["player_id"]), {
      display: String(r["display"] ?? ""), title: String(r["title"] ?? ""),
    });
  }
  const photoByPid = new Map<number, PhotoInfo>();
  for (const r of photoRows) {
    photoByPid.set(Number(r["pid"]), {
      headshot_path: r["headshot_path"] ? String(r["headshot_path"]) : null,
      team_code: r["team_code"] ? String(r["team_code"]) : null,
    });
  }
  const prospectsByName = new Map<string, ProspectMeta>();
  for (const r of prospectRows) {
    const name = `${String(r["first_name"] ?? "")} ${String(r["last_name"] ?? "")}`.trim();
    const key = name.toLowerCase();
    if (!prospectsByName.has(key)) {
      prospectsByName.set(key, {
        name, position: String(r["position"] ?? ""), club: String(r["club"] ?? ""),
        league: String(r["league"] ?? ""), nationality: String(r["nationality"] ?? ""),
        ep_url: String(r["ep_url"] ?? ""),
      });
    }
  }
  const generalSources: [string, string][] = sourceRows
    .filter((r) => r["team_code"] === null || r["team_code"] === undefined)
    .map((r) => [String(r["label"]), String(r["url"])]);

  return {
    phases, entryDraft, teams, players, trades, selections,
    statsByPid, photoByPid, prospectsByName, generalSources,
  };
}

// ---------------------------------------------------------------------------
// Draft selection meta (mirror app._draft_player_meta via prospects.find)
// ---------------------------------------------------------------------------
function selectionMeta(d: ExpansionData, sel: DraftSelection): {
  name: string; position: string; previous_team: string; nationality: string; ep_url: string;
} {
  const pro = d.prospectsByName.get(sel.player.trim().toLowerCase());
  if (pro) {
    const leagueName = LEAGUE_NAMES[pro.league] ?? pro.league;
    return {
      name: pro.name,
      position: pro.position || sel.position,
      previous_team: pro.club || leagueName || sel.previous_team,
      nationality: pro.nationality || sel.nationality,
      ep_url: pro.ep_url,
    };
  }
  return {
    name: sel.player, position: sel.position, previous_team: sel.previous_team,
    nationality: sel.nationality, ep_url: "",
  };
}

// ---------------------------------------------------------------------------
// Render: phase tracker (now-banner + stepper + key dates)
// ---------------------------------------------------------------------------
function renderTracker(d: ExpansionData, nowMs: number): string {
  const cur = currentPhase(d.phases, nowMs);
  const nxt = nextPhase(d.phases, nowMs);
  const draftState = d.entryDraft ? keydateState(d.entryDraft, nowMs) : "upcoming";

  const bannerKind = cur ? "active" : nxt ? "upcoming" : "done";
  let banner = "";
  if (cur) {
    banner = `<span class="exp-now-eyebrow">Current phase</span>
      <strong class="exp-now-title">Phase ${cur.number} — ${esc(cur.name)}</strong>
      <p class="exp-now-summary">${esc(cur.summary)}</p>`;
  } else if (nxt) {
    banner = `<span class="exp-now-eyebrow">Up next</span>
      <strong class="exp-now-title">Phase ${nxt.number} — ${esc(nxt.name)}</strong>
      <p class="exp-now-summary">Begins ${esc(monDayTime(nxt.start))} ET. ${esc(nxt.summary)}</p>`;
  } else {
    banner = `<span class="exp-now-eyebrow">Complete</span>
      <strong class="exp-now-title">The 2026 expansion distribution process has concluded.</strong>`;
  }

  const steps = d.phases.map((ph) => {
    const dateLabel = ph.number === 6
      ? `${esc(monDay(ph.start))} &rarr;`
      : `${esc(monDay(ph.start))}–${esc(monDay(ph.end))}`;
    let step = `<li class="exp-step is-${phaseState(ph, nowMs)}">
        <span class="exp-step-num">${ph.number}</span>
        <span class="exp-step-body">
          <span class="exp-step-name">${esc(ph.name)}</span>
          <span class="exp-step-dates">${dateLabel}</span>
        </span>
      </li>`;
    if (ph.number === 5 && d.entryDraft) {
      step += `<li class="exp-step exp-step--draft is-${draftState}">
          <a href="/draft" class="exp-step-link">
            <span class="exp-step-num" aria-hidden="true">&#9733;</span>
            <span class="exp-step-body">
              <span class="exp-step-name">${esc(d.entryDraft.label)}</span>
              <span class="exp-step-dates">${esc(monDay(d.entryDraft.when))} · view prospects</span>
            </span>
          </a>
        </li>`;
    }
    return step;
  }).join("");

  const keyGroups = d.phases.map((ph) => {
    const items = ph.dates.map((kd) => {
      const st = keydateState(kd, nowMs);
      return `<li class="exp-keydate is-${st}">
          <span class="exp-keydate-mark" aria-hidden="true">${st === "past" ? "&#10003;" : "&#9675;"}</span>
          <span class="exp-keydate-when">${esc(monDayTime(kd.when))} ET</span>
          <span class="exp-keydate-label">${esc(kd.label)}</span>
          <span class="exp-keydate-aud">${esc(kd.audience)}</span>
        </li>`;
    }).join("");
    let group = `<div class="exp-keydate-group is-${phaseState(ph, nowMs)}">
        <h3 class="exp-keydate-phase">Phase ${ph.number} · ${esc(ph.name)}</h3>
        <ul class="exp-keydate-list">${items}</ul>
      </div>`;
    if (ph.number === 5 && d.entryDraft) {
      const kd = d.entryDraft;
      group += `<div class="exp-keydate-group is-${draftState}">
          <h3 class="exp-keydate-phase">Entry Draft</h3>
          <ul class="exp-keydate-list">
            <li class="exp-keydate is-${draftState}">
              <span class="exp-keydate-mark" aria-hidden="true">${draftState === "past" ? "&#10003;" : "&#9675;"}</span>
              <span class="exp-keydate-when">${esc(monDay(kd.when))}</span>
              <span class="exp-keydate-label">${esc(kd.label)}</span>
              <span class="exp-keydate-aud">${esc(kd.audience)}</span>
            </li>
          </ul>
        </div>`;
    }
    return group;
  }).join("");

  return `<div class="exp-now-banner exp-now-banner--${bannerKind}">${banner}</div>
    <ol class="exp-stepper" aria-label="Expansion phases">${steps}</ol>
    <details class="exp-keydates-toggle">
      <summary>All key dates &amp; deadlines</summary>
      <div class="exp-keydates">${keyGroups}</div>
    </details>`;
}

// ---------------------------------------------------------------------------
// Render: 2026 Entry Draft board (mirror _expansion_draft.html)
// ---------------------------------------------------------------------------
function renderDraftBoard(d: ExpansionData): string {
  if (!d.entryDraft) return "";
  const rounds = draftRounds(d.trades, d.selections);
  const made = rounds.flat().filter((p) => p.selection !== null).length;
  const total = rounds.flat().length;
  const teamByCode = new Map(d.teams.map((t) => [t.code, t]));

  const tabs = rounds.map((rd, i) => {
    const rn = rd[0]!.round;
    const active = i === 0;
    return `<button type="button" class="exp-draft-tab${active ? " is-active" : ""}" role="tab"
        id="exp-draft-tab-${rn}" aria-controls="exp-draft-panel-${rn}"
        aria-selected="${active ? "true" : "false"}" data-target-round="${rn}">Round ${rn}</button>`;
  }).join("");

  const panels = rounds.map((rd, i) => {
    const rn = rd[0]!.round;
    const active = i === 0;
    const body = rd.map((pick) => renderDraftRow(d, pick, teamByCode)).join("");
    return `<div class="exp-draft-panel${active ? " is-active" : ""}" id="exp-draft-panel-${rn}"
        role="tabpanel" aria-labelledby="exp-draft-tab-${rn}"${active ? "" : " hidden"}>
        <div class="table-scroll exp-draft-scroll">
        <table class="exp-draft-table">
          <thead><tr>
            <th scope="col" class="exp-draft-num">#</th><th scope="col">Team</th>
            <th scope="col">Player</th><th scope="col">Pos</th>
            <th scope="col">Previous Team</th><th scope="col">Nationality</th>
          </tr></thead>
          <tbody>${body}</tbody>
        </table>
        </div>
      </div>`;
  }).join("");

  return `<section class="exp-draft" id="exp-draft" aria-label="2026 Entry Draft">
      <header class="exp-section-head exp-draft-head">
        <h2 class="exp-section-title">2026 Entry Draft</h2>
        <p class="exp-section-sub">
          ${esc(monthDayYear(d.entryDraft.when))} · ${made}/${total} picks made ·
          <a href="/draft">prospect board</a> ·
          <a href="https://www.thepwhl.com/en/draft" target="_blank" rel="noopener">official order →</a>
        </p>
      </header>
      <div class="exp-draft-tabs" role="tablist" aria-label="Draft round">${tabs}</div>
      ${panels}
    </section>`;
}

function renderDraftRow(d: ExpansionData, pick: DraftPick, teamByCode: Map<string, Team>): string {
  const owner = teamByCode.get(pick.owner);
  const ownerLabel = owner ? owner.code : pick.owner;
  let teamCell = `<td class="exp-draft-team">
      <span class="team-colored" style="--team-color: ${teamColor(pick.owner)}">${esc(ownerLabel)}</span>`;
  if (pick.is_traded) {
    const via = pick.trade_source
      ? `<a href="${esc(pick.trade_source)}" target="_blank" rel="noopener"${pick.trade_note ? ` title="${esc(pick.trade_note)}"` : ""}>${esc(pick.via)}</a>`
      : esc(pick.via);
    teamCell += `<span class="exp-draft-via">via ${via}</span>`;
  }
  teamCell += `</td>`;

  let rest: string;
  if (pick.selection) {
    const m = selectionMeta(d, pick.selection);
    const nameHtml = m.ep_url
      ? `<a href="${esc(m.ep_url)}" target="_blank" rel="noopener">${esc(m.name)}</a>`
      : esc(m.name);
    const src = pick.selection.source_url
      ? ` <a class="exp-draft-src" href="${esc(pick.selection.source_url)}" target="_blank" rel="noopener" aria-label="Selection source">&#9656;</a>`
      : "";
    rest = `<td class="exp-draft-player">${nameHtml}${src}</td>
      <td>${esc(m.position) || "—"}</td>
      <td>${esc(m.previous_team) || "—"}</td>
      <td>${esc(m.nationality) || "—"}</td>`;
  } else {
    rest = `<td class="exp-draft-player exp-draft-empty">—</td>
      <td class="exp-draft-empty">—</td><td class="exp-draft-empty">—</td><td class="exp-draft-empty">—</td>`;
  }
  return `<tr class="exp-draft-row${pick.selection ? " is-made" : ""}">
      <td class="exp-draft-num">${pick.overall}</td>${teamCell}${rest}</tr>`;
}

// ---------------------------------------------------------------------------
// Render: roster cards (mirror _expansion_team_card / _player_row / _profile)
// ---------------------------------------------------------------------------
function photoCell(d: ExpansionData, p: Player): string {
  const info = p.player_id !== null ? d.photoByPid.get(p.player_id) : undefined;
  const chain: [string, string][] = [];
  if (info?.headshot_path) chain.push([`/headshots/${info.headshot_path}`, "headshot"]);
  if (info?.team_code) {
    const c = info.team_code.toUpperCase();
    chain.push([`/team-assets/jersey/${c}.png`, "jersey"], [`/team-assets/logo/${c}.png`, "logo"]);
  }
  if (!chain.length) {
    return `<span class="exp-player-photo exp-player-photo--initials">
        <span class="exp-initials" aria-hidden="true">${esc(initials(p.name))}</span></span>`;
  }
  const [firstUrl, firstKind] = chain[0]!;
  const fallbacks = chain.slice(1).map(([u, k]) => `${u}::${k}`).join("|");
  return `<span class="exp-player-photo exp-player-photo--${firstKind}">
      <img src="${esc(firstUrl)}" alt="${esc(p.name)}" loading="lazy" decoding="async"
           data-fallbacks="${esc(fallbacks)}" data-initials="${esc(initials(p.name))}"></span>`;
}

function playerRow(d: ExpansionData, p: Player, viewingCode: string): string {
  const departed = isDepartedFrom(p, viewingCode);
  const pTags = tags(p);
  const incoming = p.origin_team !== viewingCode;
  const stat = p.player_id !== null ? d.statsByPid.get(p.player_id) : undefined;
  const latest = latestMove(p);

  const liClass = `exp-player${departed ? " is-departed" : ""}${pTags.length ? " is-actioned" : ""}`;
  const nameExtras = `${p.position ? `<span class="exp-player-pos">${esc(p.position)}</span>` : ""}` +
    `${p.jersey ? `<span class="exp-player-jersey">#${esc(p.jersey)}</span>` : ""}`;
  const statHtml = stat
    ? `<span class="exp-player-stats gloss-label" data-glossary="${esc(stat.title)}">${esc(stat.display)}</span>`
    : "";

  let status = "";
  if (incoming && !departed) status += `<span class="exp-move exp-move--from">${esc(p.origin_team)} &rarr;</span>`;
  if (departed) status += `<span class="exp-move exp-move--to">&rarr; ${esc(currentTeam(p))}</span>`;
  for (const [label, css] of pTags) status += `<span class="exp-tag ${esc(css)}">${esc(label)}</span>`;
  if (latest?.note) status += `<span class="exp-move-note">${esc(latest.note)}</span>`;
  if (latest?.source_url) {
    status += `<a class="exp-src" href="${esc(latest.source_url)}" target="_blank" rel="noopener" title="Source" aria-label="Source">&#8599;</a>`;
  }

  return `<li class="${liClass}">
      ${photoCell(d, p)}
      <span class="exp-player-main">
        <span class="exp-player-name">${esc(p.name)} ${nameExtras}</span>
        <span class="exp-player-contract">${esc(contractLabel(p.contract))}</span>
        ${statHtml}
      </span>
      <span class="exp-player-status">${status}</span>
    </li>`;
}

function teamCard(d: ExpansionData, team: Team): string {
  const full = rosterFor(d.players, team.code)
    .sort((a, b) => compareSortKeys(rosterSortKey(a), rosterSortKey(b)));
  const roster = full.filter((p) => !isRetired(p));
  const retired = full.filter((p) => isRetired(p));
  const departed = departedFrom(d.players, team.code)
    .sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0));
  const nPlayers = roster.length;
  const nActions = roster.filter((p) => p.moves.length > 0).length;
  const picks = draftPicksFor(d.trades, d.selections, team.code);

  const sub = team.kind === "expansion"
    ? `<strong>${nPlayers}</strong> / 10 signed`
    : `${nPlayers} players${nActions ? ` · <strong>${nActions}</strong> protected / signed` : ""}`;

  const profile = `<div class="exp-profile">
      <div class="exp-profile-head">
        <span class="exp-profile-title">Player Per-Game Average</span>
        <a class="exp-profile-link" href="/expansion/team/${esc(team.code)}">How this team compares &rarr;</a>
      </div>
      <p class="exp-profile-coverage exp-profile-coverage--empty" data-profile-for="${esc(team.code)}">
        The per-game profile builds once box scores are ingested.
      </p>
    </div>`;

  const rosterHtml = roster.length
    ? `<ul class="exp-player-list">${roster.map((p) => playerRow(d, p, team.code)).join("")}</ul>`
    : `<p class="exp-empty">${team.kind === "expansion"
        ? "No signings yet — roster fills in Phases 2 &amp; 4." : "—"}</p>`;

  const picksHtml = picks.length ? renderDraftPicksSection(d, picks) : "";
  const retiredHtml = retired.length
    ? `<div class="exp-departed exp-retired"><p class="exp-departed-label">Retired</p>
        <ul class="exp-player-list exp-player-list--retired">${retired.map((p) => playerRow(d, p, team.code)).join("")}</ul></div>`
    : "";
  const departedHtml = departed.length
    ? `<div class="exp-departed"><p class="exp-departed-label">Lost to expansion</p>
        <ul class="exp-player-list exp-player-list--departed">${departed.map((p) => playerRow(d, p, team.code)).join("")}</ul></div>`
    : "";

  // Expansion clubs (the headline) open by default; the eight existing clubs
  // start collapsed so the page isn't an enormous initial scroll.
  const openAttr = team.kind === "expansion" ? " open" : "";
  return `<details class="exp-team-card exp-team-card--${esc(team.kind)}" data-team-code="${esc(team.code)}"
        style="--team-color: ${teamColor(team.code)}; --team-color-soft: ${teamColorSoft(team.code, 0.18)};"${openAttr}>
      <summary class="exp-team-head">
        <span class="exp-team-badge" style="background: var(--team-color); color: ${teamColorContrast(team.code)};">${esc(team.code)}</span>
        <div class="exp-team-headings">
          <h3 class="exp-team-name">${esc(team.name)}${team.note ? ` <span class="exp-team-note">${esc(team.note)}</span>` : ""}</h3>
          <p class="exp-team-sub">${sub}</p>
        </div>
      </summary>
      ${profile}
      ${rosterHtml}
      ${picksHtml}
      ${retiredHtml}
      ${departedHtml}
    </details>`;
}

function renderDraftPicksSection(d: ExpansionData, picks: DraftPick[]): string {
  const items = picks.map((pick) => {
    const m = selectionMeta(d, pick.selection!);
    const badge = `R${pick.round} #${pick.overall}`;
    let sub = "";
    if (m.previous_team) {
      sub = `<span class="exp-player-contract">from ${esc(m.previous_team)}${m.nationality ? ` · ${esc(m.nationality)}` : ""}</span>`;
    } else if (m.nationality) {
      sub = `<span class="exp-player-contract">${esc(m.nationality)}</span>`;
    }
    let status = "";
    if (pick.via) status += `<span class="exp-draft-via">via ${esc(pick.via)}</span>`;
    if (pick.selection!.source_url) {
      status += `<a class="exp-draft-src" href="${esc(pick.selection!.source_url)}" target="_blank" rel="noopener" aria-label="Selection source">&#9656;</a>`;
    }
    return `<li class="exp-player exp-draft-pick">
        <span class="exp-draft-pick-badge">${esc(badge)}</span>
        <span class="exp-player-main">
          <span class="exp-player-name">${esc(m.name)}${m.position ? ` <span class="exp-player-pos">${esc(m.position)}</span>` : ""}</span>
          ${sub}
        </span>
        <span class="exp-player-status">${status}</span>
      </li>`;
  }).join("");
  return `<div class="exp-departed exp-draft-section">
      <p class="exp-departed-label">Draft picks</p>
      <ul class="exp-player-list exp-player-list--draft">${items}</ul>
    </div>`;
}

// Status-tag key. The roster rows carry up to eight differently-colored tags;
// without a legend the colors (and the EFO/FPO/ENTL acronyms) are opaque.
const LEGEND: [string, string, string][] = [
  ["Protected", "tag-protected", "On an existing club's protection list"],
  ["Re-signed", "tag-resigned", "Re-signed with the same club"],
  ["Signed", "tag-signed", "Signed to a contract"],
  ["EFO", "tag-efo", "Expansion signing mechanic (Phase 2)"],
  ["FPO", "tag-fpo", "Expansion signing mechanic (Phase 2)"],
  ["Selected", "tag-selected", "Via the Phase 2 selection process"],
  ["Qualified", "tag-qualified", "Qualifying offer / rights retained"],
  ["Retiring", "tag-retiring", "Announced retirement"],
];

function renderLegend(): string {
  const items = LEGEND.map(([label, css, desc]) =>
    `<span class="exp-legend-item"><span class="exp-tag ${css}">${esc(label)}</span>
      <span class="exp-legend-desc">${esc(desc)}</span></span>`).join("");
  return `<details class="exp-legend">
      <summary>What the status tags mean</summary>
      <div class="exp-legend-grid">${items}</div>
    </details>`;
}

function renderRosters(d: ExpansionData): string {
  const chips = d.teams.map((t) =>
    `<label class="exp-filter-chip"><input type="checkbox" data-team-code="${esc(t.code)}">
      <span class="team-colored" style="--team-color: ${teamColor(t.code)}">${esc(t.code)}</span></label>`).join("");
  const expansionCards = d.teams.filter((t) => t.kind === "expansion").map((t) => teamCard(d, t)).join("");
  const existingCards = d.teams.filter((t) => t.kind === "existing").map((t) => teamCard(d, t)).join("");

  return `<div class="exp-filter" id="exp-filter">
      <div class="exp-filter-bar" aria-label="Filter by team">
        <button type="button" class="exp-filter-action is-active" data-action="all">All teams</button>
        ${chips}
      </div>
    </div>
    ${renderLegend()}
    <header class="exp-section-head">
      <h2 class="exp-section-title">Expansion clubs</h2>
      <p class="exp-section-sub">Each fills to 10 players under contract by the end of Phase 4.</p>
    </header>
    <div class="exp-team-grid exp-team-grid--expansion">${expansionCards}</div>
    <header class="exp-section-head">
      <h2 class="exp-section-title">Existing clubs</h2>
      <p class="exp-section-sub">Protections highlighted; players lost to expansion fade out below each roster.</p>
    </header>
    <div class="exp-team-grid exp-team-grid--existing">${existingCards}</div>`;
}

function renderSources(d: ExpansionData): string {
  const items = d.generalSources.map(([label, url]) =>
    `<li><a href="${esc(url)}" target="_blank" rel="noopener">${esc(label)}</a></li>`).join("");
  return `<details>
      <summary>How this is tracked &amp; sourced</summary>
      <p class="exp-sources-note">
        Contract statuses are drawn from the league's official contract-status page.
        Phase status advances automatically by date. Roster moves are curated as
        they're announced — primary source first (the PWHL transactions page), then
        trackers and team insiders:
      </p>
      <ul class="exp-sources-list">${items}</ul>
    </details>`;
}

// ---------------------------------------------------------------------------
// Interactivity: photo fallback, team filter, draft tabs, profile summaries
// ---------------------------------------------------------------------------
function wirePhotoFallbacks(root: HTMLElement): void {
  root.querySelectorAll<HTMLImageElement>("img[data-fallbacks]").forEach((img) => {
    img.addEventListener("error", () => {
      const raw = img.getAttribute("data-fallbacks") ?? "";
      const list = raw ? raw.split("|") : [];
      const next = list.shift();
      const wrap = img.parentElement;
      if (next) {
        const [url, kind] = next.split("::");
        img.setAttribute("data-fallbacks", list.join("|"));
        if (wrap && kind) wrap.className = `exp-player-photo exp-player-photo--${kind}`;
        img.src = url!;
      } else if (wrap) {
        wrap.className = "exp-player-photo exp-player-photo--initials";
        wrap.innerHTML = `<span class="exp-initials" aria-hidden="true">${img.getAttribute("data-initials") ?? "?"}</span>`;
      }
    });
  });
}

function wireTeamFilter(root: HTMLElement): void {
  const filter = root.querySelector<HTMLElement>("#exp-filter");
  if (!filter) return;
  const chips = filter.querySelectorAll<HTMLInputElement>('input[type="checkbox"][data-team-code]');
  const cards = root.querySelectorAll<HTMLElement>(".exp-team-card[data-team-code]");
  const allBtn = filter.querySelector<HTMLElement>('.exp-filter-action[data-action="all"]');
  const apply = (): void => {
    const selected = new Set<string>();
    chips.forEach((c) => { if (c.checked) selected.add(c.dataset["teamCode"] ?? ""); });
    const none = selected.size === 0;
    cards.forEach((el) => {
      el.style.display = none || selected.has(el.dataset["teamCode"] ?? "") ? "" : "none";
    });
    if (allBtn) allBtn.classList.toggle("is-active", none);
  };
  chips.forEach((c) => c.addEventListener("change", apply));
  allBtn?.addEventListener("click", () => { chips.forEach((c) => { c.checked = false; }); apply(); });
  apply();
}

function wireDraftTabs(root: HTMLElement): void {
  const draft = root.querySelector<HTMLElement>("#exp-draft");
  if (!draft) return;
  const tabs = draft.querySelectorAll<HTMLElement>(".exp-draft-tab");
  const panels = draft.querySelectorAll<HTMLElement>(".exp-draft-panel");
  const show = (round: string): void => {
    tabs.forEach((t) => {
      const on = t.dataset["targetRound"] === round;
      t.classList.toggle("is-active", on);
      t.setAttribute("aria-selected", on ? "true" : "false");
    });
    panels.forEach((p) => {
      const on = p.id === `exp-draft-panel-${round}`;
      p.classList.toggle("is-active", on);
      p.toggleAttribute("hidden", !on);
    });
  };
  tabs.forEach((t, i) => {
    t.addEventListener("click", () => show(t.dataset["targetRound"] ?? ""));
    // Roving keyboard nav between tabs, as the role="tab" contract implies.
    t.addEventListener("keydown", (e) => {
      let next = -1;
      if (e.key === "ArrowRight" || e.key === "ArrowDown") next = (i + 1) % tabs.length;
      else if (e.key === "ArrowLeft" || e.key === "ArrowUp") next = (i - 1 + tabs.length) % tabs.length;
      else if (e.key === "Home") next = 0;
      else if (e.key === "End") next = tabs.length - 1;
      if (next < 0) return;
      e.preventDefault();
      const target = tabs[next]!;
      show(target.dataset["targetRound"] ?? "");
      target.focus();
    });
  });
}

/** Fetch each team's prebuilt profile JSON and fill in the per-game headline on
 *  its card. Mirrors app._expansion_profile_summary (after.metrics + coverage),
 *  but client-side: progressive, never blocks the roster render. */
function fillProfileSummaries(root: HTMLElement, teams: Team[]): void {
  teams.forEach((team) => {
    const el = root.querySelector<HTMLElement>(`[data-profile-for="${team.code}"]`);
    if (!el) return;
    void fetch(`/expansion-data/profiles/${team.code.toUpperCase()}.json`)
      .then((r) => (r.ok ? r.json() : null))
      .then((profile: unknown) => {
        const after = (profile as { after?: Record<string, unknown> } | null)?.after;
        if (!after || !after["n_with_stats"]) return;
        const m = (after["metrics"] as Record<string, unknown>) ?? {};
        const cov = `<span class="exp-profile-cov">(${after["n_with_stats"]} of ${after["n_skaters"]} skaters)</span>`;
        el.classList.remove("exp-profile-coverage--empty");
        if (m["g_pg"] !== null && m["g_pg"] !== undefined) {
          const pim = m["pim_pg"] !== null && m["pim_pg"] !== undefined ? ` · ${esc(String(m["pim_pg"]))} PIM/GP` : "";
          el.innerHTML = `<strong>${esc(String(m["g_pg"]))}</strong> G/GP · <strong>${esc(String(m["points_pg"]))}</strong> PTS/GP${pim} ${cov}`;
        } else {
          el.innerHTML = `Box-score profile from ${after["n_with_stats"]} of ${after["n_skaters"]} skaters.`;
        }
      })
      .catch(() => { /* leave the "builds once ingested" placeholder */ });
  });
}

// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------
export function initExpansion(): boolean {
  const trackerEl = document.getElementById("exp-tracker");
  const draftEl = document.getElementById("exp-draft-root");
  const rostersEl = document.getElementById("exp-rosters");
  const sourcesEl = document.getElementById("exp-sources");
  if (!trackerEl || !draftEl || !rostersEl || !sourcesEl) return false;

  trackerEl.innerHTML = '<p class="empty">Loading…</p>';
  void (async () => {
    try {
      const d = await loadData();
      const nowMs = Date.now();
      trackerEl.innerHTML = renderTracker(d, nowMs);
      draftEl.innerHTML = renderDraftBoard(d);
      rostersEl.innerHTML = renderRosters(d);
      sourcesEl.innerHTML = renderSources(d);
      const page = document.body;
      wirePhotoFallbacks(page);
      wireTeamFilter(page);
      wireDraftTabs(page);
      fillProfileSummaries(page, d.teams);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      trackerEl.innerHTML = `<p class="empty">Couldn’t load the expansion tracker: ${esc(msg)}</p>`;
      console.error("[expansion] load failed:", err);
    }
  })();
  return true;
}
